import React, { useState, useEffect } from "react";
import { Row, Col, Divider, Avatar, Spin, Tag, Badge } from "antd";
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  GlobalOutlined,
  HomeOutlined,
  SafetyOutlined,
  TeamOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
  CrownOutlined
} from "@ant-design/icons";
import { RESTExecutor, withDomains } from "../../lib/domain";
import { getImageUrl } from "../../util/ui_util";
import moment from "moment";

const AccountOverviewPage = ({ domains, userInfo, refreshUserData, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [invitations, setInvitations] = useState([]);

  let get_user = domains?.user ? RESTExecutor.list()
    .config({
      label: "me",
    })
    .callbacks((success) => {
      setUserData(success.result || {});
      setLoading(false);
    }, (error) => {
      console.error("Failed to load user data:", error);
      setLoading(false);
    })
    .connect(domains.user) : null;

  let list_namespace = domains?.user ? RESTExecutor.list()
    .config({
      label: "list_namespace",
    })
    .callbacks((success) => {
      setOrganizations(success.result || []);
    }, (error) => {
      console.error("Failed to load organizations:", error);
    })
    .connect(domains.user) : null;

  let namespace_invitations = domains?.user ? RESTExecutor.list()
    .config({
      label: "namespace_invitations",
      params: { status: "Pending" },
    })
    .callbacks((success) => {
      setInvitations(success.result || []);
    }, (error) => {
      console.error("Failed to load invitations:", error);
    })
    .connect(domains.user) : null;

  useEffect(() => {
    if (get_user) {
      setLoading(true);
      get_user.execute(true);
    }
    if (list_namespace) {
      list_namespace.execute(true);
    }
    if (namespace_invitations) {
      namespace_invitations.execute(true);
    }
  }, [domains]);

  const STATUS_CONFIG = {
    Active: { bg: '#DCFFE4', color: '#35B14F' },
    Confirmed: { bg: '#DCFFE4', color: '#35B14F' },
    Accepted: { bg: '#DCFFE4', color: '#35B14F' },
    Enabled: { bg: '#DCFFE4', color: '#35B14F' },
    Verified: { bg: '#DCFFE4', color: '#35B14F' },
    Pending: { bg: '#FFF6E7', color: '#FABE50' },
    Inactive: { bg: '#FFF6E7', color: '#FABE50' },
    Rejected: { bg: '#FFF6E7', color: '#FABE50' },
    Unverified: { bg: '#FFF6E7', color: '#FABE50' },
    Disabled: { bg: '#FFF6E7', color: '#FABE50' },
    Suspend: { bg: '#F1D7CA', color: '#EC6853' },
    Suspended: { bg: '#F1D7CA', color: '#EC6853' },
    Expired: { bg: '#F1D7CA', color: '#EC6853' },
    Archive: { bg: '#F1D7CA', color: '#EC6853' },
    Archived: { bg: '#F1D7CA', color: '#EC6853' },
    Demo: { bg: '#DAF8FF', color: '#48B6FF' },
    'Read-only': { bg: '#FFF6E7', color: '#FABE50' },
  };

  const ROLE_CONFIG = {
    owner: { bg: '#F0F4FF', color: '#4F46E5' },
    admin: { bg: '#F0F4FF', color: '#4F46E5' },
    user: { bg: '#F8F9FA', color: '#6B7280' },
    viewer: { bg: '#F8F9FA', color: '#6B7280' },
    accountant: { bg: '#F8F9FA', color: '#6B7280' },
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || { bg: '#F8F9FA', color: '#6B7280' };
  };

  const getRoleConfig = (role) => {
    return ROLE_CONFIG[role] || { bg: '#F8F9FA', color: '#6B7280' };
  };

  const StatusBadge = ({ status, showDot = true }) => {
    const config = getStatusConfig(status);
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: config.bg,
        padding: '4px 12px',
        borderRadius: '6px',
        height: '28px',
        width: 'fit-content'
      }}>
        {showDot && (
          <div style={{
            width: '8px',
            height: '8px',
            backgroundColor: config.color,
            borderRadius: '50%',
            marginRight: '8px'
          }} />
        )}
        <span style={{
          fontFamily: 'Inter',
          fontSize: '12px',
          fontWeight: '500',
          color: config.color
        }}>
          {status}
        </span>
      </div>
    );
  };

  const RoleBadge = ({ role, icon }) => {
    const config = getRoleConfig(role);
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: config.bg,
        padding: '4px 12px',
        borderRadius: '6px',
        height: '28px',
        width: 'fit-content',
        gap: '6px'
      }}>
        {icon && <span style={{ color: config.color, fontSize: '12px' }}>{icon}</span>}
        <span style={{
          fontFamily: 'Inter',
          fontSize: '12px',
          fontWeight: '500',
          color: config.color
        }}>
          {getRoleDisplayName(role)}
        </span>
      </div>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': '#91979F',
      'Pending': '#91979F',
      'Demo': '#91979F',
      'Expired': '#91979F',
      'Suspended': '#91979F'
    };
    return colors[status] || '#91979F';
  };

  const getStatusBackgroundColor = (status) => {
    const colors = {
      'Active': '#F8F9FA',
      'Pending': '#F8F9FA',
      'Demo': '#F8F9FA',
      'Expired': '#F8F9FA',
      'Suspended': '#F8F9FA'
    };
    return colors[status] || '#F8F9FA';
  };

  const getRoleDisplayName = (role) => {
    const roles = {
      'admin': 'Administrator',
      'owner': 'Owner', 
      'user': 'User',
      'viewer': 'Viewer',
      'accountant': 'Accountant'
    };
    return roles[role] || role;
  };

  const formatJoinDate = (dateString) => {
    return moment(dateString).format("MMMM D, YYYY");
  };

  const getUserAvatar = () => {
    if (getImageUrl(userData)) {
      return getImageUrl(userData);
    }
    const nameParts = userData.name ? userData.name.split(" ") : ["User"];
    const firstName = nameParts[0] || "User";
    const lastName = nameParts[1] || "";
    return `https://ui-avatars.com/api/?name=${firstName}+${lastName}&background=1ED760&color=fff&size=120&rounded=true`;
  };

  if (loading) {
    return (
      <div className="edit-form-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#91979F' }}>Loading account information...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="edit-form-container">
        <p className="edit-form-header">Profile Information</p>
        
        <Row align="middle" style={{ marginBottom: '24px' }}>
          <Col span={4}>
            <Avatar 
              size={80} 
              src={getUserAvatar()}
              icon={<UserOutlined />}
            />
          </Col>
          <Col span={20} style={{ paddingLeft: '16px' }}>
            <div style={{
              fontFamily: 'Inter',
              fontSize: '24px',
              fontWeight: '600',
              color: '#343C46',
              marginBottom: '4px'
            }}>
              {userData.name || 'User'}
            </div>
            <div style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: '400',
              color: '#91979F',
              marginBottom: '8px'
            }}>
              Member since {formatJoinDate(userData.created_at)}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <StatusBadge 
                status={'Enabled'} 
                showDot={true} 
              />
              {userData.email_verified && (
                <StatusBadge 
                  status="Verified" 
                  showDot={true} 
                />
              )}
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontFamily: 'Inter',
            fontSize: '16px',
            fontWeight: '600',
            color: '#343C46',
            marginBottom: '16px'
          }}>
            Contact Information
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Row align="middle">
              <Col span={2}>
                <MailOutlined style={{ fontSize: '16px', color: '#91979F' }} />
              </Col>
              <Col span={6}>
                <span style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#636972'
                }}>
                  Email:
                </span>
              </Col>
              <Col span={16}>
                <span style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  color: '#343C46'
                }}>
                  {userData.email || 'Not provided'}
                </span>
              </Col>
            </Row>

            {userData.phone_number && (
              <Row align="middle">
                <Col span={2}>
                  <PhoneOutlined style={{ fontSize: '16px', color: '#91979F' }} />
                </Col>
                <Col span={6}>
                  <span style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#636972'
                  }}>
                    Phone:
                  </span>
                </Col>
                <Col span={16}>
                  <span style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    color: '#343C46'
                  }}>
                    {userData.phone_number}
                  </span>
                </Col>
              </Row>
            )}

            {userData.address && (
              <Row align="top">
                <Col span={2}>
                  <HomeOutlined style={{ fontSize: '16px', color: '#91979F' }} />
                </Col>
                <Col span={6}>
                  <span style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#636972'
                  }}>
                    Address:
                  </span>
                </Col>
                <Col span={16}>
                  <span style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    color: '#343C46',
                    lineHeight: '1.5'
                  }}>
                    {userData.address}
                  </span>
                </Col>
              </Row>
            )}
          </div>
        </div>
      </div>

      <div className="edit-form-container">
        <div style={{ marginBottom: '16px' }}>
          <p className="edit-form-header">Your Organizations</p>
          <p style={{ 
            margin: 0, 
            color: '#91979F', 
            fontSize: '14px',
            fontFamily: 'Inter',
            fontWeight: '400'
          }}>
            You are {organizations.length > 0 ? `a member of ${organizations.length} organization${organizations.length > 1 ? 's' : ''}` : 'not a member of any organizations'}.
          </p>
        </div>

        {organizations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#91979F' }}>
            <TeamOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
            <p>No organizations found.</p>
            <p style={{ fontSize: '12px' }}>Create or join an organization to get started.</p>
          </div>
        ) : (
          organizations.map((org, index) => (
            <div key={org.id}>
              <Row align="middle" style={{ padding: '16px 0', minHeight: '80px' }}>
                <Col span={3}>
                  <Avatar 
                    size={48}
                    src={getImageUrl(org) || `https://ui-avatars.com/api/?name=${org.name?.split(" ")[0]}+${org.name?.split(" ")[1]}&background=393965&color=fff&size=48`}
                    style={{
                      backgroundColor: '#F5F5F5'
                    }}
                  >
                    {org.name ? org.name.charAt(0).toUpperCase() : 'O'}
                  </Avatar>
                </Col>
                
                <Col span={12} style={{ paddingLeft: '16px' }}>
                  <div>
                    <div style={{
                      fontFamily: 'Inter',
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#343C46',
                      marginBottom: '4px'
                    }}>
                      {org.name || org.display_name}
                    </div>
                    <div style={{
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: '400',
                      color: '#91979F',
                      marginBottom: '4px'
                    }}>
                      {org.slug}.tigg.app
                    </div>
                    {org.role && (
                      <RoleBadge 
                        role={org.role}
                        icon={org.role === 'owner' ? <CrownOutlined /> : org.role === 'admin' ? <SafetyCertificateOutlined /> : <UserOutlined />}
                      />
                    )}
                  </div>
                </Col>
                
                <Col span={9} style={{ textAlign: 'right' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <StatusBadge 
                      status={org.subscription_status || 'Active'} 
                      showDot={true} 
                    />
                    {org.expiry_date && (
                      <span style={{
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        color: '#91979F'
                      }}>
                        Expires: {moment(org.expiry_date, 'DD-MM-YYYY').format('MMM D, YYYY')}
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
              
              {index < organizations.length - 1 && (
                <Divider style={{ margin: '0' }} />
              )}
            </div>
          ))
        )}
      </div>

      {invitations.length > 0 && (
        <div className="edit-form-container">
          <div style={{ marginBottom: '16px' }}>
            <p className="edit-form-header">Pending Invitations</p>
            <p style={{ 
              margin: 0, 
              color: '#91979F', 
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: '400'
            }}>
              You have {invitations.length} pending invitation{invitations.length > 1 ? 's' : ''} to join organization{invitations.length > 1 ? 's' : ''}.
            </p>
          </div>

          {invitations.map((invitation, index) => (
            <div key={invitation.id}>
              <Row align="middle" style={{ padding: '16px 0', minHeight: '60px' }}>
                <Col span={3}>
                  <Avatar 
                    size={40}
                    style={{
                      backgroundColor: '#FFF7E6',
                      color: '#fa8c16'
                    }}
                  >
                    <MailOutlined />
                  </Avatar>
                </Col>
                
                <Col span={15} style={{ paddingLeft: '16px' }}>
                  <div>
                    <div style={{
                      fontFamily: 'Inter',
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#343C46',
                      marginBottom: '4px'
                    }}>
                      {invitation.company?.name || invitation.organization_name}
                    </div>
                    <div style={{
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: '400',
                      color: '#91979F'
                    }}>
                      Invited by {invitation.inviter?.name || invitation.inviter_email}
                    </div>
                  </div>
                </Col>
                
                <Col span={6} style={{ textAlign: 'right' }}>
                  <StatusBadge 
                    status="Pending" 
                    showDot={true} 
                  />
                </Col>
              </Row>
              
              {index < invitations.length - 1 && (
                <Divider style={{ margin: '0' }} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="edit-form-container">
        <div style={{ marginBottom: '16px' }}>
          <p className="edit-form-header">Security Overview</p>
          <p style={{ 
            margin: 0, 
            color: '#91979F', 
            fontSize: '14px',
            fontFamily: 'Inter',
            fontWeight: '400'
          }}>
            Review your account security settings and status.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Row align="middle" style={{ padding: '12px 0' }}>
            <Col span={3}>
              <SafetyOutlined
                style={{ 
                  fontSize: '20px', 
                  color: '#91979F'
                }} 
              />
            </Col>
            <Col span={15}>
              <div>
                <div style={{
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#343C46'
                }}>
                  Two-Factor Authentication
                </div>
                <div style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#91979F'
                }}>
                  {userData.two_factor_auth_enabled 
                    ? 'Your account is protected with 2FA' 
                    : 'Add an extra layer of security to your account'
                  }
                </div>
              </div>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <StatusBadge 
                status={userData.two_factor_auth_enabled ? 'Enabled' : 'Disabled'} 
                showDot={true} 
              />
            </Col>
          </Row>

          {/* <Row align="middle" style={{ padding: '12px 0' }}>
            <Col span={3}>
              <MailOutlined 
                style={{ 
                  fontSize: '20px', 
                  color: '#91979F'
                }} 
              />
            </Col>
            <Col span={15}>
              <div>
                <div style={{
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#343C46'
                }}>
                  Email Verification
                </div>
                <div style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#91979F'
                }}>
                  {userData.email_verified 
                    ? 'Your email address has been verified' 
                    : 'Please verify your email address'
                  }
                </div>
              </div>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <StatusBadge 
                status={userData.email_verified ? 'Verified' : 'Unverified'} 
                showDot={true} 
              />
            </Col>
          </Row>

          <Row align="middle" style={{ padding: '12px 0' }}>
            <Col span={3}>
              <CalendarOutlined 
                style={{ 
                  fontSize: '20px', 
                  color: '#91979F'
                }} 
              />
            </Col>
            <Col span={15}>
              <div>
                <div style={{
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#343C46'
                }}>
                  Last Password Change
                </div>
                <div style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#91979F'
                }}>
                  {userData.password_changed_at 
                    ? `Changed on ${formatJoinDate(userData.password_changed_at)}`
                    : 'Never changed since account creation'
                  }
                </div>
              </div>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: '#F0F4FF',
                color: '#4F46E5',
                padding: '4px 12px',
                borderRadius: '6px',
                height: '28px',
                width: 'fit-content',
                cursor: 'pointer'
              }}>
                <span style={{
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  View History
                </span>
              </div>
            </Col>
          </Row> */}
        </div>
      </div>
    </>
  );
};

export default withDomains(AccountOverviewPage, "user");